import { api } from './api';
import { ResponseType } from '../types/shared.types';
import {
  GetSystemProblemsParams,
  LocationPrinter,
  SystemProblem,
  SystemProblemCategory,
  SystemProblemDashboardResponse,
  SystemProblemListResponse,
  SystemProblemOpenCountResponse,
} from '../types/systemProblem.types';

/**
 * System problems - what the backend noticed on its own and wants the team to
 * see. Admin and support only. See `types/systemProblem.types` for the domain.
 */

export const SYSTEM_PROBLEMS_PAGE_SIZE = 20;

/** How many open problems the Dashboard card lists. The API allows 1-50. */
export const SYSTEM_PROBLEMS_CARD_LIMIT = 5;

/**
 * The Dashboard card on the Orders tab: the open count, the severity and
 * category counts, and the most urgent open problems of one category (all
 * when none). Unlike the rest of `/v2/dashboard/orders`, it ignores the date
 * and the location filter.
 */
export const getDashboardSystemProblems = ({
  limit = SYSTEM_PROBLEMS_CARD_LIMIT,
  category,
}: {
  limit?: number;
  category?: SystemProblemCategory | null;
} = {}) =>
  api.get<ResponseType<SystemProblemDashboardResponse>>(
    '/v2/dashboard/orders/system-problems',
    { params: { limit, ...(category ? { category } : {}) } }
  );

/** Newest first. */
export const getSystemProblems = (params: GetSystemProblemsParams) =>
  api.get<ResponseType<SystemProblemListResponse>>('/v1/system-problems', {
    params,
  });

/**
 * Just the open count. Nothing reads it today - the Dashboard card gets the
 * same number from its own call - kept for when the count is shown elsewhere.
 */
export const getSystemProblemOpenCount = () =>
  api.get<ResponseType<SystemProblemOpenCountResponse>>(
    '/v1/system-problems/open-count'
  );

/** 404 for one that does not exist. */
export const getSystemProblem = (problemId: number | string) =>
  api.get<ResponseType<SystemProblem>>(`/v1/system-problems/${problemId}`);

/** 400 "System problem is already resolved." when somebody got there first. */
export const resolveSystemProblem = (problemId: number) =>
  api.post<ResponseType<SystemProblem>>(
    `/v1/system-problems/${problemId}/resolve`
  );

/**
 * A location's tablet printers as the tablet last reported them, printers in
 * error first. Empty when the tablet has not reported any yet (an older
 * tablet app, or no printers).
 */
export const getLocationPrinters = (locationId: number | string) =>
  api.get<ResponseType<LocationPrinter[]>>(
    `/v1/locations/${locationId}/printers`
  );
